import {
  createFileRoute,
  Outlet,
  redirect,
  useMatches,
} from '@tanstack/react-router';

import { BillingGateDialog } from '@/components/billing/billing-gate-dialog';
import { CopilotSessionProvider } from '@/components/copilot/copilot-session-context';
import { RemountOnLanguageChange } from '@/components/i18n/remount-on-language-change';
import { AppShell } from '@/components/layout/app-shell';
import { OrganizationArrivalNotice } from '@/components/layout/organization-arrival-notice';
import { OrganizationUnavailableNotice } from '@/components/layout/organization-unavailable-notice';
import { ROUTES } from '@/constants/routes';
import { meQueryOptions } from '@/hooks/data/use-auth';
import { isOAuthPopup } from '@/components/connectors/use-oauth-callback';
import { applyLanguage, loadNamespaces } from '@/lib/i18n/i18n';
import { uiLanguageFor } from '@/lib/i18n/language';
import { ORGANIZATION_QUERY_PARAM } from '@/lib/current-organization';
import {
  enterLinkedOrganization,
  holdLinkedOrganization,
  takeLinkedOrganization,
  withoutOrganizationParam,
} from '@/lib/organization-link';
import { hasSeenPaywall } from '@/lib/paywall-seen';
import { getRedirectPath } from '@/lib/routes';
import type { CurrentUserResponse } from '@/types/generated';

interface LinkedLocation {
  href: string;
  search: Record<string, unknown>;
}

// A link into another organization reloads into it; one naming this tab's only loses the query.
const followOrganizationLink = (
  user: CurrentUserResponse,
  location: LinkedLocation,
): Promise<never> | undefined => {
  const linked = takeLinkedOrganization();
  if (linked !== null && linked !== user.organization_id) {
    enterLinkedOrganization(linked, location.href);
    return new Promise<never>(() => {});
  }
  if (ORGANIZATION_QUERY_PARAM in location.search) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      href: withoutOrganizationParam(location.href),
      replace: true,
    });
  }
  return undefined;
};

export const Route = createFileRoute('/_authenticated')({
  loader: () =>
    loadNamespaces('copilot', 'dashboard', 'reports', 'tour', 'wizard'),
  beforeLoad: async ({ context, location }) => {
    const popup = isOAuthPopup();
    // Before `/auth/me`: a login or failed refresh on the way resets identity, not this.
    if (!popup) holdLinkedOrganization(location.search);

    const user = await context.queryClient
      .fetchQuery(meQueryOptions)
      .catch(() => null);

    if (!user) {
      // TanStack Router uses thrown non-Error redirects as control flow.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: ROUTES.LOGIN,
        search: { redirect: location.href },
        replace: true,
      });
    }

    // The value the cache now holds, so the first frame is already in this language.
    await applyLanguage(uiLanguageFor(user));

    if (popup) return;
    // An unverified user keeps the link until verification ends in a full load.
    if (user.is_verified) await followOrganizationLink(user, location);

    const target = getRedirectPath({
      pathname: location.pathname,
      user,
      paywallSeen: hasSeenPaywall(user.organization_id),
    });
    if (target) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: target, replace: true });
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout(): React.ReactElement {
  const matches = useMatches();
  const hideShell = matches.some(
    (m) =>
      (m.staticData as { hideAppShell?: boolean } | undefined)?.hideAppShell,
  );

  return (
    <CopilotSessionProvider>
      <RemountOnLanguageChange>
        <BillingGateDialog />
        <OrganizationUnavailableNotice />
        <OrganizationArrivalNotice />
        {hideShell ? (
          <Outlet />
        ) : (
          <AppShell>
            <Outlet />
          </AppShell>
        )}
      </RemountOnLanguageChange>
    </CopilotSessionProvider>
  );
}
